import React, { useState } from "react";
import { Box, Typography, Card, CardContent, FormControl, InputLabel, Select, MenuItem, Slider, Divider } from "@mui/material";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import SettingsVoiceIcon from "@mui/icons-material/SettingsVoice";
import SpeedIcon from "@mui/icons-material/Speed";
import TuneIcon from "@mui/icons-material/Tune";
import CustomButton from "../../components/Button/CustomButton";
import SaveIcon from "@mui/icons-material/Save";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

const VoiceModels = () => {
  const [settings, setSettings] = useState({
    provider: "ElevenLabs",
    model: "eleven_multilingual_v2",
    stability: 0.5,
    similarityBoost: 0.75,
    speed: 1.0,
    pitch: 1.0,
  });

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleSave = () => {
    alert("Voice model settings saved successfully!");
  };

  const handlePreview = () => {
    alert("Playing voice preview with current settings...");
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Voice Model Configuration
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure voice synthesis settings and parameters
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <CustomButton
            icon={<PlayArrowIcon />}
            iconPosition="left"
            text="Preview Voice"
            variant="outlined"
            onClick={handlePreview}
          />
          <CustomButton
            icon={<SaveIcon />}
            iconPosition="left"
            text="Save Settings"
            onClick={handleSave}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Card sx={{ borderRadius: 2, border: "1px solid #E0E0E0" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <RecordVoiceOverIcon sx={{ color: "primary.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Provider & Model Selection
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Voice Provider</InputLabel>
                <Select
                  value={settings.provider}
                  label="Voice Provider"
                  onChange={(e) => handleChange("provider", e.target.value)}
                >
                  <MenuItem value="ElevenLabs">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        ElevenLabs
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        High-quality multilingual voice synthesis
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="Google">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Google Text-to-Speech
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Google Cloud TTS with WaveNet voices
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="Azure">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Azure Cognitive Services
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Microsoft Azure Neural TTS
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="PlayHT">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Play.ht
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Ultra-realistic AI voices
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              {settings.provider === "ElevenLabs" && (
                <FormControl fullWidth>
                  <InputLabel>ElevenLabs Model</InputLabel>
                  <Select
                    value={settings.model}
                    label="ElevenLabs Model"
                    onChange={(e) => handleChange("model", e.target.value)}
                  >
                    <MenuItem value="eleven_multilingual_v2">Multilingual v2</MenuItem>
                    <MenuItem value="eleven_monolingual_v1">Monolingual v1</MenuItem>
                    <MenuItem value="eleven_turbo_v2">Turbo v2 (Fastest)</MenuItem>
                  </Select>
                </FormControl>
              )}

              {settings.provider === "Google" && (
                <FormControl fullWidth>
                  <InputLabel>Google Voice Model</InputLabel>
                  <Select
                    value={settings.model}
                    label="Google Voice Model"
                    onChange={(e) => handleChange("model", e.target.value)}
                  >
                    <MenuItem value="en-US-Wavenet-D">WaveNet (US English)</MenuItem>
                    <MenuItem value="en-US-Neural2-A">Neural2 (US English)</MenuItem>
                    <MenuItem value="en-GB-Wavenet-A">WaveNet (UK English)</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2, border: "1px solid #E0E0E0" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <SettingsVoiceIcon sx={{ color: "primary.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Voice Characteristics
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Box>
                <Typography variant="body2" gutterBottom fontWeight={600}>
                  Stability: {settings.stability.toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Higher stability = more consistent voice, Lower stability = more expressive
                </Typography>
                <Slider
                  value={settings.stability}
                  onChange={(e, value) => handleChange("stability", value)}
                  min={0}
                  max={1}
                  step={0.01}
                  valueLabelDisplay="auto"
                  sx={{ mt: 1 }}
                />
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom fontWeight={600}>
                  Similarity Boost: {settings.similarityBoost.toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  How closely the AI should match the original voice characteristics
                </Typography>
                <Slider
                  value={settings.similarityBoost}
                  onChange={(e, value) => handleChange("similarityBoost", value)}
                  min={0}
                  max={1}
                  step={0.01}
                  valueLabelDisplay="auto"
                  sx={{ mt: 1 }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2, border: "1px solid #E0E0E0" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <SpeedIcon sx={{ color: "primary.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Speech Parameters
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Box>
                <Typography variant="body2" gutterBottom fontWeight={600}>
                  Speaking Speed: {settings.speed.toFixed(2)}x
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Adjust how fast the voice speaks (0.5x = slower, 2.0x = faster)
                </Typography>
                <Slider
                  value={settings.speed}
                  onChange={(e, value) => handleChange("speed", value)}
                  min={0.5}
                  max={2.0}
                  step={0.1}
                  valueLabelDisplay="auto"
                  marks={[
                    { value: 0.5, label: "0.5x" },
                    { value: 1.0, label: "1.0x" },
                    { value: 1.5, label: "1.5x" },
                    { value: 2.0, label: "2.0x" },
                  ]}
                  sx={{ mt: 2 }}
                />
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom fontWeight={600}>
                  Pitch: {settings.pitch.toFixed(2)}x
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Adjust voice pitch (0.5x = lower, 2.0x = higher)
                </Typography>
                <Slider
                  value={settings.pitch}
                  onChange={(e, value) => handleChange("pitch", value)}
                  min={0.5}
                  max={2.0}
                  step={0.1}
                  valueLabelDisplay="auto"
                  marks={[
                    { value: 0.5, label: "Low" },
                    { value: 1.0, label: "Normal" },
                    { value: 2.0, label: "High" },
                  ]}
                  sx={{ mt: 2 }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2, border: "1px solid #E0E0E0", backgroundColor: "#F9FAFB" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <TuneIcon sx={{ color: "info.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Current Configuration Summary
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Provider:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {settings.provider}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Model:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {settings.model}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Stability:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {settings.stability.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Similarity Boost:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {settings.similarityBoost.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Speed:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {settings.speed.toFixed(2)}x
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Pitch:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {settings.pitch.toFixed(2)}x
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
          <CustomButton
            icon={<PlayArrowIcon />}
            iconPosition="left"
            text="Preview with Sample Text"
            variant="outlined"
            onClick={handlePreview}
          />
          <CustomButton
            icon={<SaveIcon />}
            iconPosition="left"
            text="Save Configuration"
            onClick={handleSave}
            size="large"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default VoiceModels;
